'use client';

import { earlyFilterDateAtom, oddsPageAtom, pageSideMatchIdAtom } from '@/atoms/odds-page';
import { useAccountInfo } from '@/hooks/query/account';
import { useSiteStore } from '@/store/site';
import { SportMatchModel } from '@nf/types/odds';
import { itemsEmptyCheck } from '@nf/utils-common/fpw/utils';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCurrentProperties } from '@/hooks/query';
import React, { useState, useRef, useEffect } from 'react';
import { IO, MaybeMatch } from '@nf/utils-common/fpw/monadic';
import { getSortedMatchCollection } from '@/utils/odds-utils';
import { SportOddsTable } from './odds-table';
import { EmptyContentMessage } from '@/components/layout/empty-content-message';
import { useTranslations } from 'next-intl';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import { OddsTableMars } from '../odds-table-mars/odds-table';
import { PageLoadingSpinner } from '@/components/core/loading-spinner';
import { matchFilterService } from '@/services/match-filter.service';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';

export const SportOddsPage = () => {
	const filterLeagues = useSiteStore((state) => state.filterLeagues);
	const { eventMode, sport: currentSport, market: paramsMarket } = useCurrentProperties();
	const oddsData = useAtomValue(oddsPageAtom);
	const setPageSideMatchId = useSetAtom(pageSideMatchIdAtom);
	const hasSetPageSiteMatchId = useRef<boolean>(false);
	const [matchCollection, setMatchCollection] = useState<SportMatchModel[] | null>(null);
	const filterDate = useAtomValue(earlyFilterDateAtom);
	let sortNames: Array<string> = ['MatchCode', 'LeagueId'];
	const { user } = useAccountInfo();
	const t = useTranslations('General');
	const view = useAppearanceStore(state => state.view);
	const { SiteConfigsData: siteConfigsData } = useGetSiteConfigs();

	if (user?.UserSetting.EventSortType === 0) { // sortByTime Setting
		sortNames = ['Etm', 'MatchCode']
	}
	if (currentSport === 43) {
		sortNames.unshift('GTY');
	}
	const getMatchCollectionByEventMode = (matchCollection: SportMatchModel[]) => {
		if (eventMode !== 'M0') {
			const modeValue = Number(eventMode.substring(1));
			return matchCollection.filter(match => match.LDM === modeValue);
		}
		return matchCollection;
	}
	const getMatchCollectionByFilterLeague = (matchCollection: SportMatchModel[]) => {
		return itemsEmptyCheck(filterLeagues)
			.fold(
				() => matchCollection,
				() => matchCollection.filter(match => filterLeagues[0] === -1 || filterLeagues.includes(match.LeagueId))
			)
	}
	const getMatchCollectionByFilterDate = (matchCollection: SportMatchModel[]) => {
		if (paramsMarket === 'e' && filterDate !== 'ALL') {
			return matchCollection.filter(match => match.Ktm?.includes(filterDate ?? ''));
		}
		return matchCollection
	}
	const getMatchCollectionByFilterEarlyEvent = (matchCollection: SportMatchModel[]) => {
		if (siteConfigsData?.EnableFilterEvent && ['e', 't'].includes(paramsMarket)) {
			return matchCollection.filter(match => {
				return new Date(`${match.GameTime}-04:00`) < new Date(`2025-${siteConfigsData?.EnableFilterEvent}T00:00-04:00`);
			})
		}
		return matchCollection
	}

	const [
		isFilterLiveStreamer,
		isFilterLiveStreaming,
		isFilterLiveMatch,
		isFilterStatisticInfo,
		isFilterScoreboard,
		isFilterParlay,
		isFilterTimeMachine,
		isFilterSingleMatchParlay,
		isFilterFastMarkets,
		isFilterCashout
	] = useSiteStore(state => [
		state.isFilterLiveStreamer,
		state.isFilterLiveStreaming,
		state.isFilterLiveMatch,
		state.isFilterStatisticInfo,
		state.isFilterScoreboard,
		state.isFilterParlay,
		state.isFilterTimeMachine,
		state.isFilterSingleMatchParlay,
		state.isFilterFastMarkets,
		state.isFilterCashout
	]);

	const filterMatchCollectionByMatchFilter = (matchCollection: SportMatchModel[]) => matchCollection.filter(match => {
		return (
			(!isFilterLiveStreamer || matchFilterService.hasLiveStreamer(match, siteConfigsData, user)) &&
			(!isFilterLiveStreaming || matchFilterService.hasLiveStreaming(match, siteConfigsData, user)) &&
			(!isFilterLiveMatch || matchFilterService.hasLiveMatch(match, siteConfigsData, user)) &&
			(!isFilterStatisticInfo || matchFilterService.hasStatisticInfo(match, siteConfigsData, user)) &&
			(!isFilterScoreboard || matchFilterService.hasScoreboard(match)) &&
			(!isFilterParlay || matchFilterService.hasParlay(match)) &&
			(!isFilterTimeMachine || matchFilterService.hasTimeMachine(match, user)) &&
			(!isFilterSingleMatchParlay || matchFilterService.hasSingleMatchParlay(match, user)) &&
			(!isFilterFastMarkets || matchFilterService.hasFastMarkets(match)) &&
			(!isFilterCashout || matchFilterService.hasCashout(match, user))
		);
	});

	useEffect(() => {
		itemsEmptyCheck(oddsData?.MatchCollection)
			.chain(() => MaybeMatch(paramsMarket !== 'e' || filterDate !== ''))
			.fold(
				() => {
					setMatchCollection(null)
				},
				() => {
					IO.of(oddsData?.MatchCollection)
						.map(getMatchCollectionByFilterEarlyEvent)
						.map(getMatchCollectionByEventMode)
						.map(getMatchCollectionByFilterLeague)
						.map(getMatchCollectionByFilterDate)
						.map(filterMatchCollectionByMatchFilter)
						.map((matchCollection: SportMatchModel[]) => getSortedMatchCollection(matchCollection, sortNames))
						.map(setMatchCollection)
						.run()
				}
			)
	}, [
		oddsData?.MatchCollection, filterDate, filterLeagues, user?.UserSetting.EventSortType,
		isFilterLiveStreamer,
		isFilterLiveStreaming,
		isFilterLiveMatch,
		isFilterStatisticInfo,
		isFilterScoreboard,
		isFilterParlay,
		isFilterTimeMachine,
		isFilterSingleMatchParlay,
		isFilterFastMarkets,
		isFilterCashout
	])

	useEffect(() => {
		hasSetPageSiteMatchId.current = false;
	}, [filterDate])

	useEffect(() => {
		MaybeMatch(!hasSetPageSiteMatchId.current)
			.chain(() => MaybeMatch(!itemsEmptyCheck(matchCollection ?? []).isLeft))
			.fold(
				() => { },
				() => {
					const [firstMatch] = matchCollection ?? [];
					setPageSideMatchId(firstMatch.MatchId);
					hasSetPageSiteMatchId.current = true;
				}
			)
	}, [matchCollection])

	return (
		<>
			{
				matchCollection === null ? (
					<PageLoadingSpinner />
				) : matchCollection.length === 0 ? (
					<EmptyContentMessage
						type="sport"
						title={t.raw('NoGame')}
					/>
				) : view === ViewEnum.mars ? (
					<OddsTableMars
						sport={oddsData?.sportId as number ?? 0}
						market={paramsMarket}
						matchCollection={matchCollection}
						hasCollapseAllLeague={true}
					/>
				) : (
					<SportOddsTable
						sport={oddsData?.sportId as number ?? 0}
						market={paramsMarket}
						matchCollection={matchCollection}
					/>
				)
			}
		</>
	);
}